import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Breadcrumbs,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/system";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { colors } from "../../../utils";
import FolderTile from "../FolderTile";
import { useGetUserFolders } from "../../../services/queries";
import { getLoggedInUser } from "../../../utils/helpers";
import SearchField from "../../WheelGallery/WheelListing/ListComponents/SearchField";
import { useMoveFolder } from "../../../services/mutations";
import WheelListItem from "./components/WheelListItem";

const SearchContainer = styled(Box)({
  marginBottom: "20px",
  width: "100%",
});

const FolderList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  maxHeight: "400px",
  overflowY: "auto",
  marginBottom: "20px",
});

const ActionButtons = styled(DialogActions)({
  padding: "16px 24px",
  borderTop: `1px solid ${colors.lightBorder}`,
});

const BreadcrumbLink = styled(Typography)({
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
  color: colors.primary,
  fontSize: "14px",
});

const EmptyState = styled(Box)({
  textAlign: "center",
  padding: "20px",
  backgroundColor: colors.NeutralBackground,
  borderRadius: "8px",
  margin: "20px 0",
});

const MoveToFolderModal = ({
  isOpen,
  sourceType,
  sourceId,
  onMovePress,
  onClose,
  onCancelPress,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const moveFolder = useMoveFolder();

  const loggedInUser = getLoggedInUser();
  const userId = loggedInUser?.id;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentFolderId(null);
      setFolderPath([]);
      setSelectedFolderId(null);
      setSearchTerm("");
    }
  }, [isOpen]);

  // Debounce search text updates
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useGetUserFolders(
      {
        userId,
        parentId: currentFolderId,
        search: debouncedSearchText,
      },
      {
        enabled: isOpen && !!userId, // Only fetch when modal is open and userId exists
      }
    );

  const handleScroll = useCallback(
    (e) => {
      const element = e.target;
      if (
        element.scrollHeight - element.scrollTop === element.clientHeight &&
        !isFetchingNextPage &&
        hasNextPage
      ) {
        fetchNextPage();
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleFolderDoubleClick = (folder) => {
    setCurrentFolderId(folder.id);
    setFolderPath([...folderPath, folder]);
    setSearchTerm("");
    setSelectedFolderId(null);
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      // Root level
      setCurrentFolderId(null);
      setFolderPath([]);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolderId(newPath[index].id);
    }
    setSelectedFolderId(null);
  };

  const renderBreadcrumbs = () => (
    <Breadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      sx={{ mb: 2 }}
    >
      <BreadcrumbLink onClick={() => handleBreadcrumbClick(-1)}>
        My Folders
      </BreadcrumbLink>
      {folderPath.map((folder, index) => (
        <BreadcrumbLink
          key={folder.id}
          onClick={() => handleBreadcrumbClick(index)}
        >
          {folder.name}
        </BreadcrumbLink>
      ))}
    </Breadcrumbs>
  );

  const { folders, wheels } = useMemo(() => {
    if (!data?.pages) return { folders: [], wheels: [] };

    const allItems = data.pages.reduce((acc, page) => {
      if (!page?.results) return acc;
      return [...acc, ...page.results];
    }, []);

    const wheelStartIndex = allItems.findIndex((item) => item.type === 2);

    // Filter out the source folder if we're moving a folder
    const filteredItems =
      sourceType === "folder"
        ? allItems.filter((item) => item.id !== sourceId)
        : allItems;

    if (wheelStartIndex === -1) {
      return { folders: filteredItems, wheels: [] };
    }

    return {
      folders: filteredItems.slice(0, wheelStartIndex),
      wheels: filteredItems.slice(wheelStartIndex),
    };
  }, [data, sourceType, sourceId]);

  const handleMove = async () => {
    // Use selectedFolderId if a folder is selected, otherwise use currentFolderId
    const destinationId = selectedFolderId || currentFolderId;

    if (destinationId) {
      const payload = {
        destinationId,
        ...(sourceType === "wheel"
          ? { wheel_id: sourceId }
          : { folder_to_move: sourceId }),
      };

      try {
        await moveFolder.mutateAsync(payload);
        onMovePress({ sourceType, sourceId, targetFolderId: destinationId });
        onClose();
      } catch (error) {
        console.error("Error moving item:", error);
      }
    }
  };

  // Determine if move button should be enabled
  const isMoveEnabled = selectedFolderId || currentFolderId !== null;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        style={{
          fontFamily: "DM Sans",
          fontWeight: 500,
          fontSize: 24,
          color: colors.PrimaryPurple900,
        }}
      >
        Move to Folder
      </DialogTitle>
      <DialogContent>
        <SearchContainer>
          <SearchField
            value={searchTerm}
            handleChange={setSearchTerm}
            placeholder="Search folders..."
            height={40}
          />
        </SearchContainer>
        {renderBreadcrumbs()}
        <FolderList onScroll={handleScroll}>
          {isLoading ? (
            Array.from(new Array(4)).map((_, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Skeleton
                  variant="rectangular"
                  height={44}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  }}
                />
              </Box>
            ))
          ) : folders.length > 0 ? (
            folders.map((folder) => (
              <FolderTile
                key={folder.id}
                id={folder.id}
                name={folder.name}
                visibility={folder.visibility}
                galleryVisibility={folder.gallery_visibility}
                isSelected={selectedFolderId === folder.id}
                onSingleClick={() => setSelectedFolderId(folder.id)}
                onDoubleClick={() => handleFolderDoubleClick(folder)}
                hideBackgroundColor={true}
                hideThreeDotIcon={true}
                showArrowIcon={selectedFolderId === folder.id}
              />
            ))
          ) : (
            <EmptyState>
              <Typography variant="body1" color="textSecondary">
                {debouncedSearchText
                  ? "No folders found matching your search"
                  : "No folders found in this location"}
              </Typography>
            </EmptyState>
          )}

          {/* Wheels Section */}
          {wheels.length > 0 && (
            <>
              <Box sx={{ mt: 4, mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "DM Sans",
                    fontWeight: 600,
                    color: "#123051",
                  }}
                >
                  Wheels
                </Typography>
              </Box>
              {wheels.map((wheel) => (
                <Box key={wheel.id} sx={{ mb: 1 }}>
                  <WheelListItem data={wheel} />
                </Box>
              ))}
            </>
          )}

          {isFetchingNextPage && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Skeleton
                variant="rectangular"
                height={44}
                sx={{
                  borderRadius: 1,
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                }}
              />
            </Box>
          )}
        </FolderList>
      </DialogContent>
      <ActionButtons>
        <Button
          onClick={onCancelPress || onClose}
          variant="outlined"
          color="primary"
          sx={{
            textTransform: "capitalize",
            fontFamily: "DM Sans, sans-serif",
            fontSize: "14px",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleMove}
          variant="contained"
          color="primary"
          disabled={!isMoveEnabled}
          sx={{
            textTransform: "capitalize",
            fontFamily: "DM Sans, sans-serif",
            fontSize: "14px",
          }}
        >
          Move
        </Button>
      </ActionButtons>
    </Dialog>
  );
};

MoveToFolderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  sourceType: PropTypes.string.isRequired,
  sourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onMovePress: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCancelPress: PropTypes.func,
};

export default MoveToFolderModal;
